import React, { useContext, useEffect, useState } from 'react'
import { Layout } from './Layout'
import { t } from '../i18n/i18n'
import { Button, makeStyles, MessageBar, MessageBarBody, MessageBarTitle, tokens } from '@fluentui/react-components'
import { CalendarAdd24Regular } from '@fluentui/react-icons'
import dayjs from 'dayjs'
import { CalendarChart } from '../components/calendar/CalendarChart'
import { ModalActivity } from '../components/calendar/ModalActivity'
import { Activity } from '../api/schemas/schema'
import { DataContext } from '../context/DataContext'
import { getActivities } from '../helpers/calendarHelper'
import { Loader } from '../components/common/spinner/Loader'
import { PhoneList } from '../components/common/list/PhoneList'
import { Text } from '../components/common/text/Text'
import { formatDate } from '../helpers/stringHelper'
import { NavigationLink } from '../interfaces/INavigationLink'

const useStyles = makeStyles({
   messageBar: {
      width: '100%',
   },
   activityContainer: {
      borderRadius: '5px',
      boxShadow: tokens.shadow8,
      padding: '5px',
      paddingLeft: '19px',
      paddingRight: '19px',
   },
   calendarView: {
      display: 'none',
      '@media screen and (min-width: 641px)': {
         display: 'flex',
         flexDirection: 'column',
         gap: tokens.spacingVerticalXXL,
      },
   },
   chartWrapper: {
      position: 'relative',
   },
   phoneView: {
      display: 'none',
      '@media screen and (max-width: 640px)': {
         display: 'flex',
         flexDirection: 'column',
         gap: tokens.spacingVerticalXXL,
      },
   },
   phoneBtn: {
      display: 'none',
      '@media screen and (max-width: 640px)': {
         width: `calc(100% - (${tokens.spacingHorizontalL} * 2))`,
         display: 'inline-flex',
         position: 'fixed',
         bottom: '60px',
      },
   },
   displayPhoneView: {
      '@media screen and (min-width: 641px)': {
         display: 'none',
      },
   },
   addBtn: {
      height: '44px',
   },
})

export const Calendar = () => {
   const dataContext = useContext(DataContext)
   const classes = useStyles()
   const currentYear = dayjs().year()
   const [loading, setIsLoading] = useState(true)
   const [showLoader, setShowLoader] = useState(false)
   const [year, setYear] = useState({ name: currentYear, value: 1 })
   const [activities, setActivities] = useState<Activity[]>([])
   const [modalState, setModalState] = useState<{ isOpen: boolean; selectedActivity?: Activity }>({
      isOpen: false,
      selectedActivity: undefined,
   })
   useEffect(() => {
      if (!dataContext.state.organisation?.id) return
      const ac = new AbortController()
      setActivities([]) // avoid showing the previous year's activities on the new year's wheel while this fetch is in flight
      getActivities(dataContext, year.name, setActivities, setIsLoading, ac.signal)
      return () => ac.abort()
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [year, dataContext.state.organisation?.id])

   useEffect(() => {
      if (!loading) {
         setShowLoader(false)
         return
      }
      // Only show the loading overlay if the fetch is actually slow — otherwise fast fetches just blink the overlay on/off.
      const timeout = setTimeout(() => setShowLoader(true), 150)
      return () => clearTimeout(timeout)
   }, [loading])

   const onActivitySelect = (activity: Activity) => {
      setModalState({ isOpen: true, selectedActivity: activity })
   }

   // Clears activities and flags loading in the same event handler as the year change (not in the effect
   // below, which only runs after that render already committed) so the wheel never briefly renders the new
   // year's date math against the previous year's activities, and the "no activities" message never flashes
   // in the one-render gap between activities being cleared and the real fetch setting loading itself.
   const changeYear = (newYear: { name: number; value: number }) => {
      setActivities([])
      setIsLoading(true)
      setYear(newYear)
   }

   return (
      <Layout
         breadCrumbItems={[{ title: t('AnnualCalendar'), active: true, path: NavigationLink.AnnualCalendar }]}
         activeDesktopLink={NavigationLink.AnnualCalendar}
         button={
            <Button appearance="primary" onClick={() => setModalState({ isOpen: true })} icon={<CalendarAdd24Regular />} className={classes.addBtn}>
               {t('PlanActivity')}
            </Button>
         }
         overflowAuto={true}
      >
         <MessageBar className={classes.messageBar} style={{ visibility: !loading && activities.length < 1 ? 'visible' : 'hidden' }}>
            <MessageBarBody>
               <div className={classes.calendarView}>
                  <MessageBarTitle>{t('PlanActivitesMsg')}</MessageBarTitle>
               </div>
               <div className={classes.phoneView}>
                  <MessageBarTitle>{t('PlanActivitesPhoneViewMsg')}</MessageBarTitle>
               </div>
            </MessageBarBody>
         </MessageBar>
         <div className={classes.calendarView}>
            <div className={classes.chartWrapper}>
               {showLoader && <Loader opacity />}
               <CalendarChart activities={activities} year={year} setYear={changeYear} onActivitySelect={onActivitySelect} />
            </div>
            <ModalActivity modalState={modalState} setActivities={setActivities} setModalState={setModalState} currentYear={year.name} />
         </div>
         <div className={classes.displayPhoneView}>
            {showLoader ? (
               <Loader />
            ) : (
               <>
                  <PhoneList
                     items={activities}
                     onRowClick={(activity) => onActivitySelect(activity)}
                     renderBodyItem={(item: Activity) => (
                        <div className={classes.activityContainer}>
                           <span>
                              <Text weight="bold">{item.title}</Text>
                           </span>
                           <Text>{`${formatDate(item.startDate)} ${t('To')} ${formatDate(item.endDate)}`}</Text>
                           <Text>{item?.description}</Text>
                        </div>
                     )}
                  />
               </>
            )}
         </div>
      </Layout>
   )
}
